import { FC, useState } from 'react';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';

import { ActivationStatus } from '@/entities/vfo/types';
import { Checkbox } from '@/features/vfo/components/Checkbox';
import { UserCell } from '@/features/vfo/components/UserCell';
import { useCases } from '@/features/vfo/hooks/useCases';
import { useMembers } from '@/features/vfo/hooks/useMembers';
import { breakpoints, media } from '@/shared/components';
import { Button } from '@/shared/uikitV2/Button';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { AddMembersToCaseModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../../interwill/containers/ModalContainerV1';
import { ModalContainerV2 } from '../../interwill/containers/ModalContainerV2';

export const AddMembersToCaseModal: FC<AddMembersToCaseModalProps> = ({ caseId }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'addMembersToCase' });

  const [selectedMembers, setSelectedMembers] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { membersWithoutCurrentUser, members } = useMembers();
  const { addMember, findCase } = useCases();
  const { closeModal } = useModalsStore();

  const handleToggle = async (profileId: number) => {
    setSelectedMembers((prev) =>
      prev.includes(profileId) ? prev.filter((m) => m !== profileId) : [...prev, profileId],
    );
  };

  const handleAdd = async () => {
    setIsLoading(true);
    await Promise.allSettled(
      selectedMembers.map((profileId) => {
        const member = members.find((m) => m.profileId === profileId);
        if (!member?.tag) return false;
        return addMember({ caseId, profileId, tag: member?.tag, country: member?.country });
      }),
    );
    setIsLoading(false);
    closeModal();
  };
  const caseData = findCase(caseId);

  const availableMembers = membersWithoutCurrentUser.filter(
    (item) =>
      item.activationStatus === ActivationStatus.ACTIVE &&
      !caseData?.members.some((m) => m.profileId === item.profileId),
  );

  if (!availableMembers.length) {
    return (
      <ModalContainerV2 pinBottom title={t('noMembers')}>
        <EmptyContainer>
          <span>{t('noMembersText')}</span>
        </EmptyContainer>
        <ButtonsEmpty>
          <Button size="middle" onClick={closeModal}>
            {t('cancel')}
          </Button>
        </ButtonsEmpty>
      </ModalContainerV2>
    );
  }

  return (
    <ModalContainerV1 title={`${t('title')} ${caseData?.title}`}>
      <ListContainer>
        {availableMembers.map((member) => {
          if (!member.profileId) return null;
          return (
            <ListItem
              key={member.profileId}
              onClick={() => member.profileId && handleToggle(member.profileId)}
            >
              <Checkbox checked={selectedMembers.includes(member.profileId)} />
              <UserCell member={member} />
            </ListItem>
          );
        })}
      </ListContainer>
      <Buttons>
        <Button
          size="middle"
          onClick={handleAdd}
          disabled={selectedMembers.length === 0 || isLoading}
          loading={isLoading}
        >
          {t('addBtn')}
        </Button>
      </Buttons>
    </ModalContainerV1>
  );
};

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  padding: 24px;

  ${media.md}, ${breakpoints.sm} {
    padding: 0;
  }
`;

const EmptyContainer = styled.div`
  padding: 24px 0;
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  cursor: pointer;
  gap: 16px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;

  ${media.md}, ${breakpoints.sm} {
    flex-direction: column;
    margin-top: auto;
  }
`;

const ButtonsEmpty = styled(Buttons)`
  padding: 16px 0 0 0;
`;
